import { useCallback, useEffect, useRef, useState } from 'react'
import { AnimatePresence } from 'framer-motion'

import Button from '../button/Button'
import TextField from '../text-field/TextField'
import {
  confirmStepUpChallenge,
  startStepUpChallenge,
} from '../../_data/step-up'
import { resolveConfirmMessage, resolveStartMessage } from './helpers'
import { StepUpDialogProps, StepUpDialogState } from './types'
import {
  Backdrop,
  Card,
  Footer,
  Header,
  Message,
  Subtitle,
  Title,
} from './styled'

const CODE_LENGTH = 6

const createInitialState = (): StepUpDialogState => ({
  stage: 'sending',
  channel: null,
  maskedDestination: '',
  code: '',
  message: '',
  isBusy: true,
})

/**
 * Makes the renter prove they still hold the account before a bank connection
 * can be started.
 *
 * A password alone gets somebody into a QuickPass session, and connecting a bank
 * is the most consequential thing that session can do. This is the second factor
 * that stands between the two. The code is sent to the phone or email held on the
 * account, never to anything typed here, so the dialog cannot be talked into
 * mailing itself the answer.
 *
 * The dialog asks for the code as soon as it opens, because a renter who has just
 * pressed "Connect bank" has already said what they want and a second button to
 * press first would only be in the way.
 *
 * @param {StepUpDialogProps} props
 * @returns {JSX.Element | null}
 */
const StepUpDialog = ({
  isOpen,
  quickPassUserId,
  onCancel,
  onSatisfied,
}: StepUpDialogProps): JSX.Element | null => {
  const [state, setState] = useState<StepUpDialogState>(createInitialState)
  const hasRequestedRef = useRef<boolean>(false)

  const requestCode = useCallback(async (): Promise<void> => {
    setState(current => ({ ...current, isBusy: true, message: '' }))

    const result = await startStepUpChallenge(quickPassUserId)

    if (result === null) {
      setState(current => ({
        ...current,
        stage: 'blocked',
        isBusy: false,
        message: 'We could not send the code just now. Please try again.',
      }))
      return
    }

    const canContinue =
      result.outcome === 'sent' || result.outcome === 'cooldown'

    setState(current => ({
      ...current,
      stage: canContinue ? 'code' : 'blocked',
      channel: result.channel,
      maskedDestination: result.maskedDestination,
      code: result.outcome === 'sent' ? '' : current.code,
      isBusy: false,
      message: resolveStartMessage(
        result.outcome,
        result.channel,
        result.maskedDestination,
        result.retryAfterSeconds,
      ),
    }))
  }, [quickPassUserId])

  useEffect(() => {
    if (isOpen === false) {
      hasRequestedRef.current = false
      setState(createInitialState())
      return
    }

    if (hasRequestedRef.current === true) {
      return
    }

    hasRequestedRef.current = true
    void requestCode()
  }, [isOpen, requestCode])

  const handleConfirm = async (): Promise<void> => {
    setState(current => ({ ...current, isBusy: true, message: '' }))

    const result = await confirmStepUpChallenge(quickPassUserId, state.code)

    if (result === null) {
      setState(current => ({
        ...current,
        isBusy: false,
        message: 'We could not check that code just now. Please try again.',
      }))
      return
    }

    if (result.outcome === 'satisfied') {
      onSatisfied()
      return
    }

    setState(current => ({
      ...current,
      isBusy: false,
      message: resolveConfirmMessage(result.outcome, result.attemptsRemaining),
    }))
  }

  if (isOpen === false) {
    return null
  }

  return (
    <AnimatePresence>
      <Backdrop
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onCancel}
      >
        <Card
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 16 }}
          onClick={clickEvent => {
            clickEvent.stopPropagation()
          }}
        >
          <Header>
            <Title>Confirm it is you</Title>
            <Subtitle>
              Connecting a bank account needs a second check. Enter the code we
              sent you to carry on.
            </Subtitle>
          </Header>

          {state.stage === 'code' && (
            <TextField
              label="Security code"
              value={state.code}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={CODE_LENGTH}
              disabled={state.isBusy}
              onChange={changeEvent => {
                const { value } = changeEvent.target
                setState(current => ({ ...current, code: value }))
              }}
            />
          )}

          {state.message !== '' && <Message>{state.message}</Message>}

          <Footer>
            <Button
              type="button"
              variant="ghost"
              size="small"
              disabled={state.isBusy}
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="small"
              disabled={state.isBusy}
              onClick={() => {
                void requestCode()
              }}
            >
              {state.stage === 'code' ? 'Resend code' : 'Try again'}
            </Button>
            {state.stage === 'code' && (
              <Button
                type="button"
                size="small"
                isLoading={state.isBusy}
                disabled={state.code.length !== CODE_LENGTH}
                onClick={() => {
                  void handleConfirm()
                }}
              >
                Confirm
              </Button>
            )}
          </Footer>
        </Card>
      </Backdrop>
    </AnimatePresence>
  )
}

export default StepUpDialog
